import { useMemo, useState } from 'react';
import { Plus, Package, Camera, MoveVertical, Pencil, ArrowDownWideNarrow, ArrowUpWideNarrow } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api, withAuthToken } from '@/lib/api';
import { useStock } from '@/lib/queries';
import { useAuthStore } from '@/store/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { fmtMoney, todayISO } from '@/lib/format';
import { PhotoUploader } from '@/components/PhotoUploader';
import { PageHeader } from '@/components/PageHeader';
import { ru } from '@/i18n/ru';

type DateFilter = 'all' | 'day' | 'range';
type SortKey = 'date' | 'size' | 'weight' | 'sum';
type SortDir = 'asc' | 'desc';

const lotDateISO = (l: { purchase_date: string | Date }) =>
  String(l.purchase_date).slice(0, 10);

type LotForm = {
  brand: string;
  color: string;
  length_cm: number;
  structure: string;
  purchase_price_per_gram: number;
  purchase_date: string;
  initial_grams: number;
  note: string;
};

const emptyLotForm = (): LotForm => ({
  brand: '',
  color: '',
  length_cm: 50,
  structure: 'slavic',
  purchase_price_per_gram: 0,
  purchase_date: todayISO(),
  initial_grams: 0,
  note: '',
});

const lotStockValue = (l: {
  current_grams: number | string;
  initial_grams: number | string;
  purchase_price_per_gram: number | string;
}) => {
  const initial = Number(l.initial_grams);
  const current = Number(l.current_grams);
  const price = Number(l.purchase_price_per_gram);
  if (!initial || initial <= 0) return 0;
  return (current / initial) * price;
};

const lotFormFromLot = (l: any): LotForm => ({
  brand: l.brand ?? '',
  color: l.color ?? '',
  length_cm: Number(l.length_cm),
  structure: l.structure ?? 'slavic',
  purchase_price_per_gram: Number(l.purchase_price_per_gram),
  purchase_date: String(l.purchase_date).slice(0, 10),
  initial_grams: Number(l.initial_grams),
  note: l.note ?? '',
});

export const StockPage = () => {
  const { data: lots = [] } = useStock();
  const isOwner = useAuthStore((s) => s.master?.role) === 'owner';

  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [day, setDay] = useState(todayISO());
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const filteredLots = useMemo(() => {
    let list = [...lots];

    if (dateFilter === 'day' && day) {
      list = list.filter((l: any) => lotDateISO(l) === day);
    } else if (dateFilter === 'range') {
      if (rangeFrom) list = list.filter((l: any) => lotDateISO(l) >= rangeFrom);
      if (rangeTo) list = list.filter((l: any) => lotDateISO(l) <= rangeTo);
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a: any, b: any) => {
      let cmp = 0;
      if (sortKey === 'date') {
        cmp = lotDateISO(a).localeCompare(lotDateISO(b));
      } else if (sortKey === 'size') {
        cmp = Number(a.length_cm) - Number(b.length_cm);
      } else if (sortKey === 'weight') {
        cmp = Number(a.current_grams) - Number(b.current_grams);
      } else {
        cmp = lotStockValue(a) - lotStockValue(b);
      }
      if (cmp === 0) cmp = lotDateISO(b).localeCompare(lotDateISO(a));
      return cmp * dir;
    });

    return list;
  }, [lots, dateFilter, day, rangeFrom, rangeTo, sortKey, sortDir]);

  const totalGrams = filteredLots.reduce(
    (s: number, l: any) => s + Number(l.current_grams),
    0,
  );
  const totalValue = filteredLots.reduce(
    (s: number, l: any) => s + lotStockValue(l),
    0,
  );

  const subtitle =
    dateFilter === 'all'
      ? `${lots.length} партий`
      : `${filteredLots.length} из ${lots.length} партий`;

  const SortIcon = sortDir === 'asc' ? ArrowUpWideNarrow : ArrowDownWideNarrow;

  return (
    <div className="animate-in-up space-y-4">
      <PageHeader
        title="Склад волос"
        subtitle={subtitle}
        right={isOwner ? <NewLotDialog /> : undefined}
      />

      {!!lots.length && (
        <>
          <Tabs
            value={dateFilter}
            onValueChange={(v) => setDateFilter(v as DateFilter)}
          >
            <TabsList>
              <TabsTrigger value="all">Все</TabsTrigger>
              <TabsTrigger value="day">Дата</TabsTrigger>
              <TabsTrigger value="range">Период</TabsTrigger>
            </TabsList>
          </Tabs>

          {dateFilter === 'day' && (
            <input
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className="h-11 w-full rounded-xl border border-input bg-card px-4 text-base shadow-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15"
            />
          )}

          {dateFilter === 'range' && (
            <div className="flex gap-2">
              <input
                type="date"
                value={rangeFrom}
                onChange={(e) => setRangeFrom(e.target.value)}
                className="h-11 flex-1 rounded-xl border border-input bg-card px-4 text-base shadow-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15"
                aria-label="Дата от"
              />
              <input
                type="date"
                value={rangeTo}
                onChange={(e) => setRangeTo(e.target.value)}
                className="h-11 flex-1 rounded-xl border border-input bg-card px-4 text-base shadow-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15"
                aria-label="Дата до"
              />
            </div>
          )}

          <div className="flex gap-2">
            <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Сортировка" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">По дате</SelectItem>
                <SelectItem value="size">По размеру</SelectItem>
                <SelectItem value="weight">По весу</SelectItem>
                <SelectItem value="sum">По сумме</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-11 w-11 shrink-0"
              aria-label={sortDir === 'asc' ? 'По возрастанию' : 'По убыванию'}
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            >
              <SortIcon className="h-4 w-4" />
            </Button>
          </div>
        </>
      )}

      {!!filteredLots.length && (
        <div className="grid grid-cols-2 gap-2.5">
          <Card>
            <CardContent className="space-y-1 p-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Всего грамм
              </div>
              <div className="text-2xl font-bold tabular">{totalGrams.toFixed(0)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-1 p-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Стоимость
              </div>
              <div className="text-2xl font-bold tabular">{fmtMoney(totalValue)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="space-y-2">
        {filteredLots.map((l: any) => {
          const pct = Math.max(
            0,
            Math.min(100, (Number(l.current_grams) / Number(l.initial_grams)) * 100),
          );
          const low = pct < 20;
          const value = lotStockValue(l);
          return (
            <Card key={l.id} className="overflow-hidden">
              <CardContent className="space-y-3 p-3">
                <div className="flex items-start gap-3">
                  {l.photo_url ? (
                    <img
                      src={withAuthToken(l.photo_url)}
                      className="h-16 w-16 shrink-0 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                      <Package className="h-6 w-6" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">
                      {l.color}, {l.length_cm} см
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {l.brand ?? '—'} · {fmtMoney(Number(l.purchase_price_per_gram))}/
                      {ru.stock.perSlice}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      с {new Date(l.purchase_date).toLocaleDateString('ru-RU')}
                      {' · '}
                      {fmtMoney(value)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`text-2xl font-bold tabular ${
                        low ? 'text-warning' : ''
                      }`}
                    >
                      {Number(l.current_grams).toFixed(0)}
                      <span className="text-xs text-muted-foreground"> г</span>
                    </div>
                  </div>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full transition-all ${
                      low ? 'bg-warning' : 'bg-brand'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className={`grid gap-2 ${isOwner ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <MovementDialog lotId={l.id} />
                  <LotPhotoDialog lot={l} />
                  {isOwner && <EditLotDialog lot={l} />}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {!lots.length && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
                <Package className="h-6 w-6" />
              </div>
              <div>
                <div className="font-semibold">Партий нет</div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Добавьте первую партию волос
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        {!!lots.length && !filteredLots.length && (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              Нет партий за выбранный период
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const LotFormFields = ({
  form,
  setForm,
}: {
  form: LotForm;
  setForm: (f: LotForm) => void;
}) => (
  <div className="space-y-2">
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label>Цвет</Label>
        <Input
          value={form.color}
          onChange={(e) => setForm({ ...form, color: e.target.value })}
        />
      </div>
      <div>
        <Label>Длина (см)</Label>
        <Input
          type="number"
          value={form.length_cm}
          onChange={(e) => setForm({ ...form, length_cm: Number(e.target.value) })}
        />
      </div>
      <div className="col-span-2">
        <Label>Производитель</Label>
        <Input
          value={form.brand}
          onChange={(e) => setForm({ ...form, brand: e.target.value })}
        />
      </div>
      <div>
        <Label>Структура</Label>
        <Select value={form.structure} onValueChange={(v) => setForm({ ...form, structure: v })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="slavic">Славянские</SelectItem>
            <SelectItem value="natural">Натуральные</SelectItem>
            <SelectItem value="south">Южнорусские</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Дата закупки</Label>
        <Input
          type="date"
          value={form.purchase_date}
          onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
        />
      </div>
      <div>
        <Label>{ru.stock.pricePerSlice}</Label>
        <Input
          type="number"
          step="0.01"
          value={form.purchase_price_per_gram || ''}
          onChange={(e) =>
            setForm({ ...form, purchase_price_per_gram: Number(e.target.value) })
          }
        />
      </div>
      <div>
        <Label>{ru.stock.weightGrams}</Label>
        <Input
          type="number"
          step="0.1"
          value={form.initial_grams || ''}
          onChange={(e) => setForm({ ...form, initial_grams: Number(e.target.value) })}
        />
      </div>
    </div>
    <div>
      <Label>Заметка</Label>
      <Textarea
        rows={2}
        value={form.note}
        onChange={(e) => setForm({ ...form, note: e.target.value })}
      />
    </div>
  </div>
);

const NewLotDialog = () => {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const [form, setForm] = useState<LotForm>(emptyLotForm());

  const submit = async () => {
    await api.post('/hair-stock', form);
    qc.invalidateQueries({ queryKey: ['stock'] });
    setOpen(false);
    setForm(emptyLotForm());
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon-sm" aria-label="Новая партия">
          <Plus className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ru.stock.newLot}</DialogTitle>
        </DialogHeader>
        <LotFormFields form={form} setForm={setForm} />
        <Button className="w-full" size="lg" onClick={submit}>
          <Plus className="h-4 w-4" /> Создать партию
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const EditLotDialog = ({ lot }: { lot: any }) => {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const [form, setForm] = useState<LotForm>(() => lotFormFromLot(lot));

  const submit = async () => {
    await api.patch(`/hair-stock/${lot.id}`, form);
    qc.invalidateQueries({ queryKey: ['stock'] });
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setForm(lotFormFromLot(lot));
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Pencil className="h-3.5 w-3.5" /> Изменить
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ru.stock.editLot}</DialogTitle>
        </DialogHeader>
        <LotFormFields form={form} setForm={setForm} />
        <Button className="w-full" size="lg" onClick={submit}>
          Сохранить
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const MovementDialog = ({ lotId }: { lotId: string }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<'in' | 'out' | 'adjust'>('out');
  const [grams, setGrams] = useState<number>(0);
  const [note, setNote] = useState('');
  const qc = useQueryClient();

  const submit = async () => {
    await api.post(`/hair-stock/${lotId}/movements`, { type, grams, note: note || undefined });
    qc.invalidateQueries({ queryKey: ['stock'] });
    setOpen(false);
    setGrams(0);
    setNote('');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <MoveVertical className="h-3.5 w-3.5" /> Движение
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Движение по партии</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <Label>Тип</Label>
            <Select value={type} onValueChange={(v: any) => setType(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in">Приход</SelectItem>
                <SelectItem value="out">Расход</SelectItem>
                <SelectItem value="adjust">Корректировка (новый остаток)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Грамм</Label>
            <Input
              type="number"
              step="0.1"
              value={grams || ''}
              onChange={(e) => setGrams(Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Заметка</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <Button onClick={submit} className="w-full">
            Применить
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LotPhotoDialog = ({ lot }: { lot: any }) => {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="soft" size="sm" className="w-full">
          <Camera className="h-3.5 w-3.5" /> {lot.photo_url ? 'Фото' : 'Добавить'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Фото партии</DialogTitle>
        </DialogHeader>
        <PhotoUploader
          uploadUrl={`/hair-stock/${lot.id}/photo`}
          currentUrl={lot.photo_url}
          onUploaded={() => qc.invalidateQueries({ queryKey: ['stock'] })}
        />
      </DialogContent>
    </Dialog>
  );
};
